import { OpcUaCertificateDTO } from "../../serviceapi/dataCollectorApi";
import NotificationPopUp from "util/NotificationPopUp";
import Utils from "util/Utils";

/**
 * Certificate kinds returned by GET /rest/opcua/certificates. The spec types `kind` as a
 * free-form string, so these are declared here rather than imported from the generated client.
 */
export const OpcUaCertificateKind = {
  TRUSTED_APP: "TRUSTED_APP",
  ISSUER: "ISSUER",
  USER: "USER",
  REJECTED: "REJECTED",
} as const;

export const PASSWORD_LENGTH = 20;

/** Shortest password the create/change dialogs will accept. */
export const PASSWORD_MIN_LENGTH = 8;

const PASSWORD_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*-_=+?";

/** Cryptographically strong password, drawn without modulo bias from PASSWORD_CHARSET. */
export const generatePassword = (length: number = PASSWORD_LENGTH): string => {
  const max = Math.floor(256 / PASSWORD_CHARSET.length) * PASSWORD_CHARSET.length;
  const result: string[] = [];

  while (result.length < length) {
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);

    bytes.forEach((byte) => {
      if (result.length < length && byte < max) {
        result.push(PASSWORD_CHARSET[byte % PASSWORD_CHARSET.length]);
      }
    });
  }

  return result.join("");
};

export type PasswordStrength = {
  label: string;
  /** 0-100, for the meter width. */
  score: number;
  color: string;
};

export const evaluatePasswordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return { label: "", score: 0, color: "#6c757d" };
  }

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((pattern) =>
    pattern.test(password)
  ).length;

  // Rough entropy estimate: length weighted by how many character classes are in play.
  const score = Math.min(100, (password.length / 20) * 60 + classes * 10);

  if (score < 40) return { label: "Weak", score, color: "#ef4444" };
  if (score < 70) return { label: "Moderate", score, color: "#f59e0b" };
  return { label: "Strong", score, color: "#22c55e" };
};

/** Long hex thumbprints are unreadable in a table cell; show the ends only. */
export const formatThumbprint = (thumbprint?: string): string => {
  if (!thumbprint) return "—";
  if (thumbprint.length <= 20) return thumbprint;
  return `${thumbprint.slice(0, 8)}…${thumbprint.slice(-8)}`;
};

export const formatCreatedAt = (createdAt?: string): string =>
  createdAt ? Utils.formatISOToBasicDateFormat(createdAt) : "—";

/**
 * Error reporter for the requests sent with `responseType: "blob"`. Axios puts a Blob in
 * `error.response.data` for those, and NotificationPopUp.handleGenericError only looks at a
 * `message` property — so without unwrapping, a rejected download or issue request always
 * reports "Unspecified error" instead of the server's reason.
 */
export const reportBlobRequestError = async (error: any, summary: string) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    const text = await data.text().catch(() => "");

    try {
      const parsed = JSON.parse(text);
      error.response.data = parsed?.message != null ? parsed : { message: text };
    } catch {
      // Not JSON — pass the raw body through so the toast still says something useful.
      error.response.data = text ? { message: text } : null;
    }
  }

  NotificationPopUp.handleGenericError(error, summary);
};

/** Strip characters that would be awkward or unsafe in a download filename. */
export const toSafeFileName = (value: string, fallback: string): string => {
  const safe = value.replace(/[^a-zA-Z0-9._-]/g, "_").replace(/^_+|_+$/g, "");
  return safe || fallback;
};

/** Best-effort human label for a certificate row, used in confirmations and filenames. */
export const certificateLabel = (certificate: OpcUaCertificateDTO): string =>
  certificate.username ||
  certificate.subject ||
  formatThumbprint(certificate.thumbprint);

export const kindSeverity = (
  kind?: string
): "success" | "warning" | "info" | "danger" | null => {
  switch (kind) {
    case OpcUaCertificateKind.TRUSTED_APP:
      return "success";
    case OpcUaCertificateKind.REJECTED:
      return "warning";
    case OpcUaCertificateKind.ISSUER:
      return "info";
    case OpcUaCertificateKind.USER:
      return "info";
    default:
      return null;
  }
};

export const kindLabel = (kind?: string): string => {
  switch (kind) {
    case OpcUaCertificateKind.TRUSTED_APP:
      return "Trusted";
    case OpcUaCertificateKind.REJECTED:
      return "Rejected";
    case OpcUaCertificateKind.ISSUER:
      return "Issuer";
    case OpcUaCertificateKind.USER:
      return "User";
    default:
      return kind || "Unknown";
  }
};

/** Summary toast for a batch of sequential calls, e.g. "Approved 2 of 3 certificates (1 failed)". */
export const showBulkResult = (verb: string, succeeded: number, total: number) => {
  const failed = total - succeeded;
  const noun = total === 1 ? "certificate" : "certificates";
  const detail = `${verb} ${succeeded} of ${total} ${noun}${
    failed > 0 ? ` (${failed} failed)` : ""
  }.`;

  NotificationPopUp.show(detail, failed > 0 ? "warn" : "success");
};
